/**
 * Enhanced Product Quantity Calculator Utility
 * Handles case/unit calculations with proper inventory constraints
 */

import { tokenManager } from '../services/api.js';
import { getSpecialUnitPrice, getSpecialUnitFeeSuppressed } from '../store/specialPricingStore.js';

export class ProductQuantityCalculator {
  constructor(product) {
    this.product = product;
    this.unitsPerCase = Math.max(1, product?.unitsPerCase || 1);
    // Sell type: 1 = Unit Only, 2 = Case Only, 3 = Cases & Units
    const optSellByTypeID = product?.optSellByTypeID;
    this.canSellByUnit = optSellByTypeID === 1 || optSellByTypeID === 3;
    this.canSellByCase = optSellByTypeID === 2 || optSellByTypeID === 3;
    this.unitAvailable = Math.max(0, product?.unitAvailable || 0);
    // MOQ pricing tiers
    this.moqPricingTiers = product?.productSkuVariantMOQPricing || [];

    // Customer-specific special price (Special Pricing module), if any. Prefer a value
    // stamped on the product; otherwise look it up by variant id from the special-pricing
    // store. Becomes one extra "lowest-wins" candidate in the pricing methods below.
    const special = product?.specialUnitPrice != null
      ? Number(product.specialUnitPrice)
      : getSpecialUnitPrice(product?.id);
    this.specialUnitPrice = special != null && special > 0 ? special : null;
    // Whether the winning special-pricing list waives the broken-case fee (applies
    // only on lines where the special price actually wins — see calculatePrice).
    this.specialSuppressUnitFee = product?.specialSuppressUnitFee != null
      ? !!product.specialSuppressUnitFee
      : getSpecialUnitFeeSuppressed(product?.id);

    // Custom case/unit labels
    const allowCustomLabels = this._getAllowCustomLabels();
    this.caseLabel = (allowCustomLabels && product?.caseLabel) ? product.caseLabel : 'Case';
    this.unitLabel = (allowCustomLabels && product?.unitLabel) ? product.unitLabel : 'Unit';
    this.caseLabelPlural = this.caseLabel + 's';
    this.unitLabelPlural = this.unitLabel + 's';
  }

  _getAllowCustomLabels() {
    // Check company from store (customers) or direct company (employees)
    const store = tokenManager.getCurrentStore();
    if (store?.company?.allowCustomCaseUnitLabels) return true;
    const company = tokenManager.getCurrentCompany();
    return company?.allowCustomCaseUnitLabels || false;
  }

  get soldByText() {
    if (this.canSellByCase && this.canSellByUnit) return `${this.caseLabelPlural} & ${this.unitLabelPlural}`;
    if (this.canSellByUnit) return `${this.unitLabel} Only`;
    return `${this.caseLabel} Only`;
  }

  /**
   * Get the base (quantity-1) unit price: the lowest of the regular unitPrice,
   * the discountUnitPrice, and the customer special price. This is the lowest-wins
   * candidate before MOQ volume tiers are considered.
   * @returns {number} Base unit price
   */
  getBaseUnitPrice() {
    const product = this.product;
    if (!product) return 0;
    let best = product.unitPrice || 0;
    if (product.discountUnitPrice && product.discountUnitPrice > 0 && product.discountUnitPrice < best) {
      best = product.discountUnitPrice;
    }
    if (this.specialUnitPrice != null && this.specialUnitPrice < best) {
      best = this.specialUnitPrice;
    }
    return best;
  }

  /**
   * Get the original unit price (before any discounts)
   * @returns {number} Original unit price
   */
  getOriginalUnitPrice() {
    return this.product?.unitPrice || 0;
  }

  /**
   * Check if a lower price applies (discount OR customer special price), i.e. the
   * effective base price is below the regular unit price. Drives the crossed-out
   * "original price" display.
   * @returns {boolean} True when the base price beats the regular unit price
   */
  hasDiscountPrice() {
    const product = this.product;
    if (!product) return false;
    return this.getBaseUnitPrice() < (product.unitPrice || 0);
  }

  /**
   * Check if product has MOQ (volume) pricing tiers
   * @returns {boolean} True if MOQ pricing tiers exist
   */
  hasMOQPricing() {
    return this.moqPricingTiers.length > 0;
  }

  /**
   * Resolve the unit price based on quantity and applicable tiers
   * @param {number} totalUnits - Total units being purchased
   * @returns {object} { price, tier, isMOQ, tierLabel }
   */
  resolveUnitPrice(totalUnits) {
    const safeTotal = Math.max(0, totalUnits || 0);
    // Base price already folds in discount + customer special price (lowest of the three).
    const basePrice = this.getBaseUnitPrice();

    // Find the highest MOQ tier that applies to this quantity, if any.
    let applicableTier = null;
    if (this.hasMOQPricing()) {
      const sortedTiers = [...this.moqPricingTiers].sort(
        (a, b) => b.unitQuantity - a.unitQuantity
      );
      for (const tier of sortedTiers) {
        if (safeTotal >= tier.unitQuantity) {
          applicableTier = tier;
          break;
        }
      }
    }

    // Lowest wins: an MOQ tier only applies if it beats the base (discount/special) price.
    if (applicableTier && applicableTier.unitPrice < basePrice) {
      return {
        price: applicableTier.unitPrice,
        tier: applicableTier.unitQuantity,
        isMOQ: true,
        tierLabel: applicableTier.quantityLabel || `${applicableTier.unitQuantity}+ units`
      };
    }

    return {
      price: basePrice,
      tier: null,
      isMOQ: false,
      tierLabel: null
    };
  }

  /**
   * Get all pricing tiers formatted for display, including base price
   * @returns {array} Array of tier objects with quantity ranges, prices, and savings
   */
  getMOQPricingTiers() {
    const originalPrice = this.getOriginalUnitPrice();
    const basePrice = this.getBaseUnitPrice();
    const tiers = [];

    // Sort MOQ tiers ascending by quantity
    const sortedMOQTiers = [...this.moqPricingTiers].sort(
      (a, b) => a.unitQuantity - b.unitQuantity
    );

    // Add base tier (before any MOQ kicks in)
    const firstMOQQuantity = sortedMOQTiers.length > 0 ? sortedMOQTiers[0].unitQuantity : null;

    tiers.push({
      id: 'base',
      minQuantity: 1,
      maxQuantity: firstMOQQuantity ? firstMOQQuantity - 1 : null,
      unitPrice: basePrice,
      originalPrice: originalPrice,
      savingsPercent: originalPrice > 0 ? Math.round((1 - basePrice / originalPrice) * 100) : 0,
      savingsPerUnit: originalPrice - basePrice,
      label: firstMOQQuantity ? `1-${firstMOQQuantity - 1}` : '1+',
      isBase: true,
      isMOQ: false
    });

    // Add MOQ tiers
    sortedMOQTiers.forEach((tier, index) => {
      const nextTier = sortedMOQTiers[index + 1];
      const maxQty = nextTier ? nextTier.unitQuantity - 1 : null;

      tiers.push({
        id: tier.id || `moq-${index}`,
        minQuantity: tier.unitQuantity,
        maxQuantity: maxQty,
        unitPrice: tier.unitPrice,
        originalPrice: originalPrice,
        savingsPercent: originalPrice > 0 ? Math.round((1 - tier.unitPrice / originalPrice) * 100) : 0,
        savingsPerUnit: originalPrice - tier.unitPrice,
        label: maxQty ? `${tier.unitQuantity}-${maxQty}` : `${tier.unitQuantity}+`,
        quantityLabel: tier.quantityLabel,
        isBase: false,
        isMOQ: true
      });
    });

    return tiers;
  }

  /**
   * Get the active tier for a given quantity
   * @param {number} totalUnits - Total units
   * @returns {object|null} The active tier object or null
   */
  getActiveTier(totalUnits) {
    const tiers = this.getMOQPricingTiers();
    const safeTotal = Math.max(0, totalUnits || 0);

    // Find the tier where quantity falls within range
    for (let i = tiers.length - 1; i >= 0; i--) {
      const tier = tiers[i];
      if (safeTotal >= tier.minQuantity) {
        return tier;
      }
    }

    return tiers[0] || null;
  }

  /**
   * Convert total units to cases and individual units
   * @param {number} totalUnits - Total number of units
   * @returns {object} { cases, units, totalUnits }
   */
  unitsToDisplay(totalUnits) {
    const safeTotal = Math.max(0, totalUnits || 0);
    
    if (!this.canSellByUnit || this.unitsPerCase === 1) {
      return {
        cases: Math.floor(safeTotal / this.unitsPerCase),
        units: 0,
        totalUnits: safeTotal,
      };
    }

    const cases = Math.floor(safeTotal / this.unitsPerCase);
    const units = safeTotal % this.unitsPerCase;

    return {
      cases,
      units,
      totalUnits: safeTotal,
    };
  }

  /**
   * Convert cases and units to total units
   * @param {number} cases - Number of cases
   * @param {number} units - Number of individual units
   * @returns {number} Total units
   */
  displayToUnits(cases, units) {
    const safeCases = Math.max(0, cases || 0);
    const safeUnits = Math.max(0, units || 0);
    const totalFromCases = safeCases * this.unitsPerCase;
    const totalUnits = totalFromCases + safeUnits;
    return Math.max(0, totalUnits);
  }

  /**
   * Enhanced quantity validation with inventory constraints
   * @param {number} cases - Number of cases
   * @param {number} units - Number of individual units
   * @returns {object} { isValid, error, maxUnits, adjustedQuantity }
   */
  validateQuantity(cases, units) {
    const safeCases = Math.max(0, cases || 0);
    const safeUnits = Math.max(0, units || 0);
    const totalUnits = this.displayToUnits(safeCases, safeUnits);

    // Basic validations
    if (safeCases < 0 || safeUnits < 0) {
      return {
        isValid: false,
        error: "Quantities cannot be negative",
        maxUnits: 0,
        adjustedQuantity: { cases: 0, units: 0 }
      };
    }

    // Check if units are purchasable
    if (!this.canSellByUnit && safeUnits > 0) {
      return {
        isValid: false,
        error: "This product is sold by the case only",
        maxUnits: safeCases * this.unitsPerCase,
        adjustedQuantity: { cases: safeCases, units: 0 }
      };
    }

    // Check individual units limit
    if (this.canSellByUnit && safeUnits >= this.unitsPerCase) {
      const adjustedCases = safeCases + Math.floor(safeUnits / this.unitsPerCase);
      const adjustedUnits = safeUnits % this.unitsPerCase;

      return {
        isValid: false,
        error: `That's more than a full case — we've adjusted it for you`,
        maxUnits: totalUnits,
        adjustedQuantity: { cases: adjustedCases, units: adjustedUnits },
        canAutoCorrect: true
      };
    }

    // Check inventory availability
    if (totalUnits > this.unitAvailable) {
      const maxDisplay = this.unitsToDisplay(this.unitAvailable);

      return {
        isValid: false,
        error: `Only ${this.unitAvailable} left — we've updated your quantity`,
        maxUnits: this.unitAvailable,
        adjustedQuantity: maxDisplay,
        canAutoCorrect: true,
        isInventoryLimit: true
      };
    }

    return {
      isValid: true,
      error: null,
      maxUnits: totalUnits,
      adjustedQuantity: { cases: safeCases, units: safeUnits }
    };
  }

  /**
   * Get maximum cases that can be ordered considering inventory
   * @param {number} currentUnitQuantity - Current individual units selected
   * @returns {number} Maximum cases available
   */
  getMaxCases(currentUnitQuantity = 0) {
    const safeUnits = Math.max(0, currentUnitQuantity || 0);
    const unitsAvailableForCases = Math.max(0, this.unitAvailable - safeUnits);
    return Math.floor(unitsAvailableForCases / this.unitsPerCase);
  }

  /**
   * Get maximum individual units that can be ordered considering inventory
   * @param {number} currentCaseQuantity - Current cases selected
   * @returns {number} Maximum individual units available
   */
  getMaxUnits(currentCaseQuantity = 0) {
    if (!this.canSellByUnit) return 0;
    
    const safeCases = Math.max(0, currentCaseQuantity || 0);
    const unitsUsedByCases = safeCases * this.unitsPerCase;
    const unitsAvailableForIndividual = Math.max(0, this.unitAvailable - unitsUsedByCases);
    
    // Can't exceed units per case - 1 for individual units
    return Math.min(this.unitsPerCase - 1, unitsAvailableForIndividual);
  }

  /**
   * Get display text for quantity with inventory context
   * @param {number} totalUnits - Total units
   * @returns {string} Display text
   */
  getQuantityDisplayText(totalUnits) {
    const safeTotal = Math.max(0, totalUnits || 0);
    
    if (safeTotal === 0) return "Not in cart";

    const { cases, units } = this.unitsToDisplay(safeTotal);

    if (!this.canSellByUnit || this.unitsPerCase === 1) {
      const caseCount = Math.floor(safeTotal / this.unitsPerCase);
      return `${caseCount} ${caseCount === 1 ? "case" : "cases"}`;
    }

    const parts = [];
    if (cases > 0) {
      parts.push(`${cases} ${cases === 1 ? "case" : "cases"}`);
    }
    if (units > 0) {
      parts.push(`${units} ${units === 1 ? "unit" : "units"}`);
    }

    return parts.join(" + ") || "0";
  }

  /**
   * Get next valid quantity increment with inventory constraints
   * @param {number} currentTotal - Current total units
   * @param {number} increment - Increment amount (positive or negative)
   * @returns {number} New total units
   */
  getNextValidQuantity(currentTotal, increment) {
    const safeTotal = Math.max(0, currentTotal || 0);
    const newTotal = Math.max(0, safeTotal + increment);
    
    // Clamp to available inventory
    return Math.min(newTotal, this.unitAvailable);
  }

  /**
   * Smart increment with inventory awareness
   * @param {number} currentTotal - Current total units
   * @returns {number} New total units
   */
  smartIncrement(currentTotal) {
    const safeTotal = Math.max(0, currentTotal || 0);
    
    // Check if we can increment at all
    if (safeTotal >= this.unitAvailable) {
      return safeTotal; // Already at maximum
    }

    const { cases, units } = this.unitsToDisplay(safeTotal);

    if (!this.canSellByUnit) {
      // Case-only: increment by full case if available
      const nextCaseTotal = (cases + 1) * this.unitsPerCase;
      return Math.min(nextCaseTotal, this.unitAvailable);
    }

    // Unit-purchasable: increment units first, then wrap to next case
    if (units < this.unitsPerCase - 1) {
      return Math.min(safeTotal + 1, this.unitAvailable);
    } else {
      // At max units for this case level, go to next case
      const nextCaseTotal = (cases + 1) * this.unitsPerCase;
      return Math.min(nextCaseTotal, this.unitAvailable);
    }
  }

  /**
   * Smart decrement with validation
   * @param {number} currentTotal - Current total units
   * @returns {number} New total units
   */
  smartDecrement(currentTotal) {
    const safeTotal = Math.max(0, currentTotal || 0);
    
    if (safeTotal === 0) return 0;

    const { cases, units } = this.unitsToDisplay(safeTotal);

    if (!this.canSellByUnit) {
      // Case-only: decrement by full case
      return Math.max(0, (cases - 1) * this.unitsPerCase);
    }

    // Unit-purchasable: decrement units first
    if (units > 0) {
      return safeTotal - 1;
    } else if (cases > 0) {
      // No individual units, decrement from cases
      return (cases - 1) * this.unitsPerCase + (this.unitsPerCase - 1);
    }

    return 0;
  }

  /**
   * Calculate price with enhanced fee logic, MOQ pricing, and inventory context
   * @param {number} totalUnits - Total units
   * @returns {object} Enhanced price information
   */
  calculatePrice(totalUnits) {
    const safeTotal = Math.max(0, totalUnits || 0);
    const originalUnitPrice = this.getOriginalUnitPrice();
    const unitsPerCase = this.unitsPerCase;

    // Resolve the actual unit price based on quantity (MOQ tiers or base price)
    const resolvedPricing = this.resolveUnitPrice(safeTotal);
    const effectiveUnitPrice = resolvedPricing.price;

    // When the special-pricing price wins and its list waives the broken-case fee,
    // suppress the fee for this line (matches the backend resolver + admin modal).
    const specialWins = this.specialUnitPrice != null && effectiveUnitPrice === this.specialUnitPrice;
    const singleUnitFee = (specialWins && this.specialSuppressUnitFee)
      ? 0
      : parseFloat(this.product?.singleUnitFee || 0);

    const totalPrice = safeTotal * effectiveUnitPrice;

    // Break-case fee logic: Only apply fees if total units < unitsPerCase
    const isPartialCase = safeTotal < unitsPerCase && safeTotal > 0;
    const totalFees = isPartialCase ? safeTotal * singleUnitFee : 0;

    const grandTotal = totalPrice + totalFees;

    // Calculate savings compared to original price
    const originalTotalPrice = safeTotal * originalUnitPrice;
    const totalSavings = originalTotalPrice - totalPrice;
    const savingsPercent = originalTotalPrice > 0
      ? Math.round((totalSavings / originalTotalPrice) * 100)
      : 0;

    return {
      // Price info
      unitPrice: effectiveUnitPrice,
      originalUnitPrice,
      totalPrice,
      fees: totalFees,
      grandTotal,
      // Savings info
      totalSavings,
      savingsPercent,
      hasSavings: totalSavings > 0,
      // MOQ/Tier info
      isMOQPrice: resolvedPricing.isMOQ,
      activeTier: resolvedPricing.tier,
      tierLabel: resolvedPricing.tierLabel,
      // Fee info
      hasFeesPerUnit: singleUnitFee > 0 && isPartialCase,
      isPartialCase,
      feePerUnit: singleUnitFee,
      // Inventory context
      exceedsInventory: safeTotal > this.unitAvailable,
      inventoryConstrainedTotal: Math.min(safeTotal, this.unitAvailable),
      inventoryConstrainedPrice: Math.min(safeTotal, this.unitAvailable) * effectiveUnitPrice
    };
  }

  /**
   * Get comprehensive inventory constraints for UI
   * @returns {object} Inventory constraint information
   */
  getInventoryConstraints() {
    return {
      unitAvailable: this.unitAvailable,
      unitsPerCase: this.unitsPerCase,
      canSellByUnit: this.canSellByUnit,
      maxTotalUnits: this.unitAvailable,
      maxFullCases: Math.floor(this.unitAvailable / this.unitsPerCase),
      maxIndividualUnits: this.canSellByUnit ? 
        Math.min(this.unitsPerCase - 1, this.unitAvailable) : 0,
      inStock: this.unitAvailable > 0,
      isOutOfStock: this.unitAvailable === 0,
      hasPartialCaseAvailable: this.unitAvailable > 0 && this.unitAvailable < this.unitsPerCase,
      availabilityText: this.getAvailabilityText(),
      constraintText: this.getConstraintText()
    };
  }

  /**
   * Get availability text for display
   * @returns {string} Availability description
   */
  getAvailabilityText() {
    if (this.unitAvailable === 0) {
      return "Out of stock";
    }
    
    const fullCases = Math.floor(this.unitAvailable / this.unitsPerCase);
    const remainingUnits = this.unitAvailable % this.unitsPerCase;
    
    if (fullCases === 0) {
      return `${this.unitAvailable} individual units available`;
    } else if (remainingUnits === 0) {
      return `${fullCases} full ${fullCases === 1 ? 'case' : 'cases'} available`;
    } else {
      return `${fullCases} ${fullCases === 1 ? 'case' : 'cases'} + ${remainingUnits} units available`;
    }
  }

  /**
   * Get purchasing constraint text
   * @returns {string} Constraint description
   */
  getConstraintText() {
    if (!this.canSellByUnit) {
      return `Sold by case only (${this.unitsPerCase} units per case)`;
    }

    if (this.unitsPerCase > 1) {
      return `${this.unitsPerCase} units per case - individual units available`;
    }

    return "Individual units available";
  }

  /**
   * Check if the current state allows for quantity changes
   * @param {number} currentTotal - Current total units
   * @returns {object} Capability information
   */
  getQuantityCapabilities(currentTotal = 0) {
    const safeTotal = Math.max(0, currentTotal || 0);
    
    return {
      canIncrement: safeTotal < this.unitAvailable,
      canDecrement: safeTotal > 0,
      canIncrementByCase: (safeTotal + this.unitsPerCase) <= this.unitAvailable,
      canIncrementByUnit: this.canSellByUnit && (safeTotal + 1) <= this.unitAvailable,
      remainingCapacity: Math.max(0, this.unitAvailable - safeTotal),
      isAtMaximum: safeTotal >= this.unitAvailable,
      isAtMinimum: safeTotal === 0
    };
  }

  /**
   * Auto-correct invalid quantity to closest valid value
   * @param {number} cases - Requested cases
   * @param {number} units - Requested units  
   * @returns {object} Corrected quantity and correction info
   */
  autoCorrectQuantity(cases, units) {
    const validation = this.validateQuantity(cases, units);
    
    if (validation.isValid) {
      return {
        cases,
        units,
        totalUnits: this.displayToUnits(cases, units),
        wasCorrected: false,
        correctionReason: null
      };
    }

    if (validation.canAutoCorrect) {
      return {
        cases: validation.adjustedQuantity.cases,
        units: validation.adjustedQuantity.units,
        totalUnits: this.displayToUnits(validation.adjustedQuantity.cases, validation.adjustedQuantity.units),
        wasCorrected: true,
        correctionReason: validation.error,
        originalRequest: { cases, units }
      };
    }

    // Fall back to zero if can't auto-correct
    return {
      cases: 0,
      units: 0,
      totalUnits: 0,
      wasCorrected: true,
      correctionReason: validation.error,
      originalRequest: { cases, units }
    };
  }

  /**
   * Check if quantity represents full cases only
   * @param {number} totalUnits - Total units
   * @returns {boolean} True if full cases only
   */
  isFullCasesOnly(totalUnits) {
    const safeTotal = Math.max(0, totalUnits || 0);
    return safeTotal % this.unitsPerCase === 0;
  }

  /**
   * Get suggested quantities based on available inventory
   * @returns {array} Array of suggested quantity objects
   */
  getSuggestedQuantities() {
    const suggestions = [];
    
    if (this.unitAvailable === 0) return suggestions;

    // Suggest full cases first
    const maxCases = Math.floor(this.unitAvailable / this.unitsPerCase);
    for (let i = 1; i <= Math.min(5, maxCases); i++) {
      suggestions.push({
        displayText: `${i} ${i === 1 ? 'case' : 'cases'}`,
        totalUnits: i * this.unitsPerCase,
        cases: i,
        units: 0,
        type: 'case'
      });
    }

    // Suggest partial quantities if units purchasable
    if (this.canSellByUnit && this.unitAvailable < this.unitsPerCase) {
      const partialUnits = this.unitAvailable;
      suggestions.push({
        displayText: `${partialUnits} ${partialUnits === 1 ? 'unit' : 'units'}`,
        totalUnits: partialUnits,
        cases: 0,
        units: partialUnits,
        type: 'units'
      });
    }

    return suggestions.slice(0, 5); // Limit to 5 suggestions
  }
}

/**
 * Helper functions for use in React components
 */

/**
 * Create a calculator instance for a product
 * @param {object} product - Product data
 * @returns {ProductQuantityCalculator} Calculator instance
 */
export const createQuantityCalculator = (product) => {
  return new ProductQuantityCalculator(product);
};

/**
 * Enhanced utility functions for common calculations
 */
export const quantityUtils = {
  /**
   * Convert units to display format with inventory validation
   */
  formatQuantity: (totalUnits, unitsPerCase, canSellByUnit, unitAvailable = Infinity) => {
    const calc = new ProductQuantityCalculator({
      unitsPerCase,
      // optSellByTypeID: 2 means case-only, anything else (like 1) means can sell by unit
      optSellByTypeID: canSellByUnit ? 1 : 2,
      unitAvailable
    });
    return calc.getQuantityDisplayText(totalUnits);
  },

  /**
   * Convert cases + units to total with validation
   */
  casesToUnits: (cases, units, unitsPerCase) => {
    const safeCases = Math.max(0, cases || 0);
    const safeUnits = Math.max(0, units || 0);
    return safeCases * unitsPerCase + safeUnits;
  },

  /**
   * Convert total units to cases + units
   */
  unitsToCases: (totalUnits, unitsPerCase) => {
    const safeTotal = Math.max(0, totalUnits || 0);
    const cases = Math.floor(safeTotal / unitsPerCase);
    const units = safeTotal % unitsPerCase;
    return { cases, units };
  },

  /**
   * Validate if quantity is valid for product with inventory
   */
  isValidQuantity: (totalUnits, product) => {
    const calc = new ProductQuantityCalculator(product);
    const { cases, units } = calc.unitsToDisplay(totalUnits);
    const validation = calc.validateQuantity(cases, units);
    return validation.isValid;
  },

  /**
   * Get maximum allowed quantity for product
   */
  getMaxAllowedQuantity: (product) => {
    const calc = new ProductQuantityCalculator(product);
    return calc.unitAvailable;
  },

  /**
   * Auto-correct quantity to fit constraints
   */
  correctQuantity: (totalUnits, product) => {
    const calc = new ProductQuantityCalculator(product);
    const { cases, units } = calc.unitsToDisplay(totalUnits);
    return calc.autoCorrectQuantity(cases, units);
  }
};